import { motion } from 'framer-motion'
import { Search, MessagesSquare, CircleCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import { buttonVariants } from './ui/button'

const steps = [
  {
    icon: Search,
    title: 'Search & Discover',
    description: 'Browse services by category or location using our intuitive search and map interface.'
  },
  {
    icon: MessagesSquare,
    title: 'Connect & Book',
    description: 'Message providers directly, check availability, and book your service with just a few clicks.'
  },
  {
    icon: CircleCheck,
    title: 'Track & Complete',
    description: 'Follow your service provider in real-time and complete your booking with secure payment.'
  }
]

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            How it works
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Getting the help you need is simple with our three-step process.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="text-center relative"
            >
              {/* Step number */}
              <div className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl mx-auto mb-6">
                {index + 1}
              </div>

              {/* Connecting line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-primary/15 to-accent/10 transform translate-x-8"></div>
              )}

              {/* Icon */}
              <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-4">
                <step.icon className="w-6 h-6 text-muted-foreground" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-foreground mb-2">{step.title}</h3>
              <p className="text-muted-foreground">{step.description}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Ready to get started?
            </h3>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Join thousands of people who have already discovered the easiest way to find and book local services.
            </p>
            <Link to="/enter" className={buttonVariants({ size: 'lg', className: 'h-12 px-8 text-base' })}>Start Exploring</Link>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default HowItWorks